class CameraCapture extends HTMLElement {
  protected readonly root: ShadowRoot;
  private devices: MediaDeviceInfo[] = [];
  private currentDeviceId: string | null = null;
  private stream: MediaStream | null = null;
  private video!: HTMLVideoElement;
  private canvas!: HTMLCanvasElement;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.root.innerHTML = `
      <style>
        :host { display: block; }
        *, *::before, *::after { box-sizing: border-box; }

        .video-wrap {
          position: relative;
          border-radius: 6px;
          overflow: hidden;
          background: #1a1a1a;
        }
        video {
          width: 100%;
          display: block;
          /* cap height so the Scan button is always visible on small phones */
          max-height: min(55vh, 400px);
          object-fit: cover;
          cursor: pointer;
        }

        /* settings gear — overlaid top-right of the video */
        .settings-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: rgba(42, 38, 34, 0.55);
          border: none;
          color: #faf7f0;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          transition: background .15s, transform .12s;
          -webkit-tap-highlight-color: transparent;
        }
        .settings-btn:hover  { background: rgba(42, 38, 34, 0.75); }
        .settings-btn:active { transform: scale(.9); }

        .controls { margin-top: 10px; }
        #snap {
          display: block;
          width: 100%;
          padding: 13px 22px;
          border-radius: 5px;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 15px;
          border: none;
          cursor: pointer;
          background: var(--kiln, #C97A2E);
          color: var(--paper-light, #FAF7F0);
          transition: background .15s, transform .12s;
          -webkit-tap-highlight-color: transparent;
        }
        #snap:hover  { background: var(--kiln-dark, #A15F1F); }
        #snap:active { transform: translateY(1px); }
      </style>

      <div class="video-wrap">
        <video autoplay playsinline muted></video>
        <button class="settings-btn" id="settings" aria-label="Camera settings" style="display:none;">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/>
          </svg>
        </button>
      </div>

      <div class="controls">
        <button id="snap">Scan</button>
      </div>

      <canvas style="display:none;"></canvas>
    `;

    this.video  = this.root.querySelector<HTMLVideoElement>('video')!;
    this.canvas = this.root.querySelector<HTMLCanvasElement>('canvas')!;

    this.video.onclick = () => this.capture();
    this.root.querySelector<HTMLElement>('#snap')!.onclick = () => this.capture();
    this.root.querySelector<HTMLElement>('#settings')!.onclick = () => {
      this.dispatchEvent(new CustomEvent('camera-settings', { bubbles: true, composed: true }));
    };

    this.init();
  }

  async init() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.devices = devices.filter(d => d.kind === 'videoinput');

      const settingsBtn = this.root.querySelector<HTMLElement>('#settings');
      if (settingsBtn) settingsBtn.style.display = this.devices.length < 2 ? 'none' : 'flex';

      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } }
      });
      this.video.srcObject = this.stream;

      const activeId = this.stream.getVideoTracks()[0]?.getSettings().deviceId;
      this.currentDeviceId = activeId ?? this.devices[0]?.deviceId ?? null;
    } catch (err) {
      console.error('Camera init failed:', err);
    }
  }

  async switchCamera() {
    if (this.devices.length < 2) return;

    const index = this.devices.findIndex(d => d.deviceId === this.currentDeviceId);
    const next = (index + 1) % this.devices.length;
    this.currentDeviceId = this.devices[next].deviceId;

    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop());
    }

    this.stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: { exact: this.currentDeviceId } }
    });

    this.video.srcObject = this.stream;
  }

  capture() {
    if (!this.video.videoWidth) return;

    const ctx = this.canvas.getContext('2d')!;
    this.canvas.width  = this.video.videoWidth;
    this.canvas.height = this.video.videoHeight;
    ctx.drawImage(this.video, 0, 0);

    this.canvas.toBlob(
      (blob) => {
        if (!blob) return;
        this.dispatchEvent(new CustomEvent('photo-captured', {
          detail: blob,
          bubbles: true,
          composed: true
        }));
      },
      'image/jpeg',
      0.8
    );
  }

  disconnectedCallback() {
    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop());
    }
  }
}

customElements.define('nu-camera-capture', CameraCapture);